export interface RuntimeFlagMap {
  [key: string]: boolean;
}

const CACHE_TTL_MS = 5000;

let cache: { at: number; flags: RuntimeFlagMap } | null = null;
let pending: Promise<RuntimeFlagMap> | null = null;

function parseRuntimeFlags(body: unknown): RuntimeFlagMap {
  if (typeof body !== 'object' || body === null) return {};
  const flags = (
    'flags' in body && typeof body.flags === 'object' && body.flags !== null
      ? body.flags
      : body
  ) as Record<string, unknown>;
  const result: RuntimeFlagMap = {};
  for (const [key, value] of Object.entries(flags)) {
    if (typeof value === 'boolean') result[key] = value;
  }
  return result;
}

async function fetchRuntimeFlags(): Promise<RuntimeFlagMap> {
  try {
    const response = await fetch('/api/feature-flags', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return {};
    return parseRuntimeFlags(await response.json());
  } catch {
    return {};
  }
}

/**
 * Current runtime flag map on the client. Shared single-flight so many
 * components read the same request; fails open when Manifest is unreachable.
 */
export async function getRuntimeFlags(): Promise<RuntimeFlagMap> {
  const now = Date.now();
  if (cache && now - cache.at < CACHE_TTL_MS) return cache.flags;
  if (pending) return pending;
  pending = fetchRuntimeFlags()
    .then((flags) => {
      cache = { at: Date.now(), flags };
      return flags;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** Runtime flag value for a key on the client, falling back when unset. */
export async function isRuntimeFlagEnabled(
  key: string,
  fallback = false,
): Promise<boolean> {
  const flags = await getRuntimeFlags();
  const value = flags[key];
  return typeof value === 'boolean' ? value : fallback;
}

/** Clears the window between tests. */
export function clearRuntimeFlagsCache(): void {
  cache = null;
  pending = null;
}
